import { useEffect, useRef, useState } from "react";
import { listActiveSales, searchCollections, type CollectionOption, type ListSalesOptions } from "../api/atomicApi";
import { buySale } from "../api/actions";
import { AssetCard } from "../components/AssetCard";
import { HotCollections } from "../components/HotCollections";
import { NeonSign } from "../components/NeonSign";
import { WaxPriceBox } from "../components/WaxPriceBox";
import { WaxVsPolygon } from "../components/WaxVsPolygon";
import { classifyCollection, type CollectionCategory } from "../utils/collectionCategory";
import { useWallet } from "../wallet/WalletContext";
import type { MarketSale } from "../types";
import { showError } from "../components/Dialog";

const PAGE_SIZE = 24;

// The Marketplace pulls from every active listing across all of
// AtomicMarket (see the note on the fetch effect below), and WAX doesn't
// give collections an official genre/category — so "sort" and "backed
// only" are the two dimensions this site can actually filter/organize by
// honestly, plus narrowing to one collection at a time.
type SortOption = "newest" | "price_asc" | "price_desc";

const SORT_TO_API: Record<SortOption, Pick<ListSalesOptions, "sort" | "order">> = {
  newest: { sort: "created", order: "desc" },
  price_asc: { sort: "price", order: "asc" },
  price_desc: { sort: "price", order: "desc" },
};

// "Backed only" isn't a field the AtomicMarket sales endpoint can filter by
// server-side — backed_tokens lives on the asset, not the sale row, and
// there's no query param for it. So this fetches raw pages and filters
// client-side, pulling additional raw pages automatically until it has a
// full page of backed matches (or gives up) rather than showing whatever
// tiny fraction of one raw page happened to be backed. Backed items are a
// small slice of chain-wide volume, so without this a visitor could click
// "backed only" and see zero results even though plenty exist further in.
const MAX_RAW_PAGES_PER_FETCH = 12;

function isBacked(sale: MarketSale): boolean {
  return (sale.assets[0]?.backed_tokens?.length ?? 0) > 0;
}

function matchesCategory(sale: MarketSale, category: CollectionCategory): boolean {
  return classifyCollection(sale.collection_name, sale.assets[0]?.collection?.name) === category;
}

const CATEGORY_SINGULAR_LABEL: Record<CollectionCategory, string> = {
  games: "game",
  tokens: "token",
  "non-games": "non-game",
};

export function Marketplace() {
  const [sales, setSales] = useState<MarketSale[]>([]);
  const [rawPage, setRawPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busySaleId, setBusySaleId] = useState<string | null>(null);
  const [collectionFilter, setCollectionFilter] = useState<string | null>(null);
  const [collectionLabel, setCollectionLabel] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<SortOption>("newest");
  const [backedOnly, setBackedOnly] = useState(false);
  const [category, setCategory] = useState<CollectionCategory>("games");
  const { session, account, connect } = useWallet();

  // Collection search combobox state.
  const [collectionQuery, setCollectionQuery] = useState("");
  const [suggestions, setSuggestions] = useState<CollectionOption[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const query = collectionQuery.trim();
    if (!query) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      searchCollections(query)
        .then((results) => {
          if (!cancelled) setSuggestions(results);
        })
        .catch(() => {
          if (!cancelled) setSuggestions([]);
        });
    }, 250); // debounce — don't fire a request on every keystroke
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [collectionQuery]);

  // Close the suggestions dropdown on an outside click.
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function selectCollection(option: CollectionOption) {
    setCollectionFilter(option.collectionName);
    setCollectionLabel(option.displayName);
    setCollectionQuery(option.displayName);
    setShowSuggestions(false);
  }

  function clearCollectionFilter() {
    setCollectionFilter(null);
    setCollectionLabel(null);
    setCollectionQuery("");
  }

  // Fetches raw sale pages starting at `startPage` and returns up to
  // PAGE_SIZE matching sales (all of them if backedOnly is off, otherwise
  // only the backed ones), plus how many raw pages it had to consume and
  // whether the underlying API still has more to give.
  async function fetchMatchingPage(
    startPage: number,
    opts: { collectionFilter: string | null; sortOption: SortOption; backedOnly: boolean; category: CollectionCategory }
  ): Promise<{ matches: MarketSale[]; nextRawPage: number; apiExhausted: boolean }> {
    const matches: MarketSale[] = [];
    let page = startPage;
    let apiExhausted = false;

    for (let scanned = 0; scanned < MAX_RAW_PAGES_PER_FETCH; scanned++) {
      const data = await listActiveSales({
        limit: PAGE_SIZE,
        page,
        collectionName: opts.collectionFilter ?? undefined,
        ...SORT_TO_API[opts.sortOption],
      });
      page += 1;
      if (data.length < PAGE_SIZE) apiExhausted = true;

      const filtered = data.filter((s) => matchesCategory(s, opts.category) && (!opts.backedOnly || isBacked(s)));
      matches.push(...filtered);

      if (apiExhausted) break;
      if (matches.length >= PAGE_SIZE) break;
    }

    return { matches: matches.slice(0, PAGE_SIZE), nextRawPage: page, apiExhausted };
  }

  // Deliberately not filtered by collection/seller by default — this pulls
  // from every listing across all of AtomicMarket (any collection's
  // creator can list here, not just yours), the same pool AtomicHub itself
  // browses. Picking a card in the Hottest Collections strip above, or
  // searching a collection below, narrows it to just that collection.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Clear out the previous filter/category's results immediately so a
    // switch (e.g. Games -> Tokens) never briefly shows the old category's
    // cards underneath the "Loading listings…" message while the new
    // fetch is in flight.
    setSales([]);
    fetchMatchingPage(1, { collectionFilter, sortOption, backedOnly, category })
      .then(({ matches, nextRawPage, apiExhausted }) => {
        if (cancelled) return;
        setSales(matches);
        setRawPage(nextRawPage);
        setHasMore(!apiExhausted);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load listings");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [collectionFilter, sortOption, backedOnly, category]);

  async function loadMore() {
    setLoadingMore(true);
    try {
      const { matches, nextRawPage, apiExhausted } = await fetchMatchingPage(rawPage, {
        collectionFilter,
        sortOption,
        backedOnly,
        category,
      });
      setSales((prev) => [...prev, ...matches]);
      setRawPage(nextRawPage);
      setHasMore(!apiExhausted);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load more listings");
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleBuy(sale: MarketSale) {
    if (!session) {
      await connect();
      return;
    }
    setBusySaleId(sale.sale_id);
    try {
      await buySale(session, sale);
      setSales((prev) => prev.filter((s) => s.sale_id !== sale.sale_id));
    } catch (err) {
      showError(err, "Purchase failed");
    } finally {
      setBusySaleId(null);
    }
  }

  return (
    <div className="page">
      <div className="homepage-topline">
        <NeonSign />
        <WaxPriceBox />
      </div>
      <div className="page__header">
        <h1>Marketplace</h1>
        <p className="page__subtitle">
          Browse and trade NFTs — items with a 🔒 badge are redeemable for locked WAX/EOS by burning them.
        </p>
      </div>

      <WaxVsPolygon />

      <div className="category-tabs" role="tablist" aria-label="Browse by category">
        <button
          type="button"
          role="tab"
          aria-selected={category === "games"}
          className={"category-tab" + (category === "games" ? " category-tab--active" : "")}
          onClick={() => setCategory("games")}
        >
          🎮 Games
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={category === "tokens"}
          className={"category-tab" + (category === "tokens" ? " category-tab--active" : "")}
          onClick={() => setCategory("tokens")}
        >
          🪙 Tokens
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={category === "non-games"}
          className={"category-tab" + (category === "non-games" ? " category-tab--active" : "")}
          onClick={() => setCategory("non-games")}
        >
          🖼️ Non-Games
        </button>
      </div>
      <p className="category-tabs__note">
        No collection on WAX carries an official genre — this split is a best-effort guess, not a certainty. Something in
        the wrong tab? Let me know which collection and I'll correct it.
      </p>

      <HotCollections
        category={category}
        activeCollection={collectionFilter}
        onSelect={(name) => {
          setCollectionFilter(name);
          setCollectionLabel(name);
          setCollectionQuery(name);
        }}
        onClear={clearCollectionFilter}
      />

      <div className="marketplace-filters">
        <div className="marketplace-filters__search" ref={searchBoxRef}>
          <label htmlFor="collection-search">Collection</label>
          <input
            id="collection-search"
            type="text"
            placeholder="Search collections…"
            value={collectionQuery}
            onChange={(e) => {
              setCollectionQuery(e.target.value);
              setShowSuggestions(true);
              if (e.target.value.trim() === "") clearCollectionFilter();
            }}
            onFocus={() => setShowSuggestions(true)}
            autoComplete="off"
          />
          {showSuggestions && suggestions.length > 0 && (
            <div className="marketplace-filters__suggestions">
              {suggestions.map((option) => (
                <button
                  key={option.collectionName}
                  type="button"
                  className="marketplace-filters__suggestion"
                  onClick={() => selectCollection(option)}
                >
                  <span>{option.displayName}</span>
                  {option.displayName !== option.collectionName && (
                    <span className="marketplace-filters__suggestion-sub">{option.collectionName}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <label className="marketplace-filters__sort">
          Sort by
          <select value={sortOption} onChange={(e) => setSortOption(e.target.value as SortOption)}>
            <option value="newest">Newest listed</option>
            <option value="price_asc">Price: low to high</option>
            <option value="price_desc">Price: high to low</option>
          </select>
        </label>

        <label className="marketplace-filters__checkbox">
          <input type="checkbox" checked={backedOnly} onChange={(e) => setBackedOnly(e.target.checked)} />
          🔒 Backed only
        </label>
      </div>

      {collectionFilter && !loading && (
        <div className="state-message">
          Showing listings from <strong>{collectionLabel ?? collectionFilter}</strong> only —{" "}
          <button className="link-button" onClick={clearCollectionFilter}>
            clear filter
          </button>
        </div>
      )}

      {loading && <div className="state-message">Loading listings…</div>}
      {error && (
        <div className="state-message state-message--error">
          Couldn't load listings from the AtomicHub API: {error}
        </div>
      )}
      {!loading && !error && sales.length === 0 && (
        <div className="state-message">
          {backedOnly
            ? `No backed ${CATEGORY_SINGULAR_LABEL[category]} listings found — try clearing the collection filter or turning off Backed only.`
            : `No active ${CATEGORY_SINGULAR_LABEL[category]} listings right now.`}
        </div>
      )}

      <div className="asset-grid">
        {sales.map((sale) => {
          const asset = sale.assets[0];
          if (!asset) return null;
          const price = `${(Number(sale.price.amount) / 10 ** sale.price.token_precision).toFixed(
            sale.price.token_precision
          )} ${sale.price.token_symbol}`;
          return (
            <AssetCard
              key={sale.sale_id}
              asset={asset}
              price={price}
              actionLabel={sale.seller === account ? "Your listing" : busySaleId === sale.sale_id ? "Buying…" : "Buy"}
              actionDisabled={sale.seller === account || busySaleId === sale.sale_id}
              onAction={() => handleBuy(sale)}
            />
          );
        })}
      </div>

      {!loading && !error && hasMore && sales.length > 0 && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button className="btn btn--outline" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
